import type {
  AuthMeResponse,
  JwtTokens,
  TokenClaims,
  Workspace,
  WorkspaceRole,
} from "@/definitions/types";
import axios, { CanceledError } from "axios";
import dayjs from "dayjs";
import { jwtDecode } from "jwt-decode";
import { parse } from "qs";
import { API_HOST } from "@/constants/envs";
import router from "@/router";
import { ROLE_RANK, useAuthStore } from "@/stores/auth";
import { pendingRequests, stringifyParams } from "@/utils/apis";
import { logger } from "@/utils/logger";
import {
  assertTabSession,
  deferRemovalWithoutWorkspace,
  deferWorkspaceRemoval,
  editedFieldVanished,
  expireSession,
  sessionNotice,
} from "@/utils/session";
import { forgetWorkspaceViewState, tokenStorage } from "@/utils/storage";
import { toastWarning } from "@/utils/toaster";

function formatPath(val: string): string {
  const _path = val.split("?");
  const uri = _path.splice(0, 1);
  return uri + stringifyParams(parse(_path.join("?")));
}

export async function routerPush(path: string): Promise<void> {
  if (formatPath(router.currentRoute.value.fullPath) === formatPath(path)) {
    return;
  }

  await router.push(path);
}

export async function routerReplace(path: string): Promise<void> {
  if (formatPath(router.currentRoute.value.fullPath) === formatPath(path)) {
    return;
  }

  await router.replace(path);
}

export function isExpiredToken(token: string): boolean {
  try {
    const { exp } = jwtDecode(token);
    if (exp === undefined) return true;
    return dayjs(exp * 1000).isBefore(dayjs());
  } catch (e: unknown) {
    logger.warn("Token validation failed:", e);
    return true;
  }
}

/**
 * 토큰이 유효하지 않을 때의 내부 정리 경로(세션 만료 · 갱신 실패). 서버 로그아웃은 호출하지 않는다. 사용자 의도가 아닌 이동이라 열어 둔 화면을
 * `?redirect=` 로 실어 로그인 뒤 돌아오게 한다(25차 A25-03). 사용자가 누르는 로그아웃은 `logout()` 이고 redirect 를 싣지 않는다.
 * 저장하지 않은 편집이 있으면 바로 보내지 않고 만료 안내부터 띄운다(`expireSession`, 30차 S30-02).
 */
export async function signOut(): Promise<void> {
  try {
    await expireSession(currentPathForRedirect());
  } catch (error) {
    logger.error("Sign out failed:", error);
  }
}

/**
 * 사용자가 직접 누르는 로그아웃.
 * - `DELETE api/v1/auth/logout` 으로 서버의 리프레시 토큰을 폐기한 뒤 로컬 세션을 정리한다.
 * - 서버 호출이 실패해도 로컬 정리와 로그인 페이지 이동은 그대로 진행한다.
 */
export async function logout(): Promise<void> {
  try {
    const accessToken = tokenStorage.getAccessToken();
    if (accessToken) {
      await authAxios().delete("api/v1/auth/logout", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    }
  } catch (e: unknown) {
    logger.warn("Logout request failed:", e);
  } finally {
    await goLoginPage();
  }
}

/**
 * 로그인 화면으로 보낸다. `redirect`(로그인하지 않고 열려던 화면의 `fullPath`)를 주면 `?redirect=` 로 실어, 로그인한 뒤 그 화면으로
 * 돌아오게 한다(25차 M25-01 — 메일 딥링크 `/operations/interview-prep?jobId=&kitId=` 가 로그인 뒤 대시보드로 흩어지지 않게).
 *
 * 이미 로그인 화면으로 가는 중이면 아무것도 하지 않는다(30차). `clearAuth` 가 화면을 인증 없이 다시 그리면서 낸 조회가 401 로 이 함수를
 * 다시 부르는데, 따로 이동하면 사용자가 누른 로그아웃(redirect 없음)이 뒤따른 이동의 `?redirect=` 로 덮였다. 진행 중인 이동을 기다리지는
 * 않는다 — 그 이동의 이탈 가드가 낸 요청에서 다시 불리면 서로를 기다려 멈춘다.
 */
let loginNavigation: Promise<void> | null = null;

/** 로그인 화면 `?reason=` 값 — 세션이 끝나서(로그인 만료) 온 것. 메일 딥링크로 온 첫 로그인과 안내 문장을 가른다(83차 JR83-12). */
export const LOGIN_REASON_EXPIRED = "expired";

/**
 * 로그인 화면 `?reason=` 값 — 소속이 하나도 남지 않게 워크스페이스 구성원에서 제거돼 온 것(90차 AU90-06 · C90-03c). 「만료」 가 아니라 제거라는 사실을
 * 로그인 화면이 말하게 한다. 열어 둔 화면으로 돌아갈 곳이 없어 `?redirect=` 는 싣지 않는다.
 */
export const LOGIN_REASON_REMOVED = "removed";

/** `goLoginPage` · `expireSession` 이 실을 수 있는 로그인 사유 */
export type LoginReason =
  typeof LOGIN_REASON_EXPIRED | typeof LOGIN_REASON_REMOVED;

/**
 * `reason` 을 주면 `?reason=` 으로 실어 로그인 화면이 왜 왔는지 말하게 한다(세션 만료 경로만 — 로그아웃 · 메일 딥링크 첫 로그인은 주지 않는다).
 */
export async function goLoginPage(
  redirect?: string,
  reason?: LoginReason,
): Promise<void> {
  if (loginNavigation) return;
  // 이미 「제거」 사유로 로그인 화면에 와 있으면 같은 순간 끝나는 다른 요청의 만료 이동이 사유를 「만료」 로 덮지 않게 한다(90차 C90-03c)
  const here = router.currentRoute.value;
  if (
    reason !== LOGIN_REASON_REMOVED &&
    here.path === "/login" &&
    here.query.reason === LOGIN_REASON_REMOVED
  ) {
    return;
  }
  const { clearAuth } = useAuthStore();
  clearAuth();
  // 이 탭이 기억한 화면 문맥(마지막 직무 · 콘솔 필터, 73차 DA73-03 · 01)은 그 세션의 것이라 함께 지운다.
  forgetWorkspaceViewState();
  const target = safeRedirectPath(redirect);
  const query = [
    target && target !== "/" ? `redirect=${encodeURIComponent(target)}` : "",
    reason ? `reason=${reason}` : "",
  ].filter(Boolean);
  loginNavigation = routerReplace(
    query.length ? `/login?${query.join("&")}` : "/login",
  ).finally(() => {
    loginNavigation = null;
  });
  await loginNavigation;
}

/**
 * 지금 보고 있는 화면의 경로(`?redirect=` 로 실을 값). 세션이 끊겨 로그인으로 보내는 경로(401 · 토큰 갱신 실패 · 만료된 저장 세션)가 쓴다
 * (25차 M25-01 수정 라운드). 초기 내비게이션이 끝나기 전에는 `currentRoute` 가 시작 위치(`/`)라 주소창 값을 쓴다.
 */
export function currentPathForRedirect(): string {
  const current = router.currentRoute.value;
  // 이미 로그인 화면이면(그 위에서 다시 불린 경우) 그 화면이 들고 있는 `?redirect=` 를 이어받는다 — 공개 화면 경로 자신은 거절되기 때문이다.
  const carried = current.query.redirect;
  if (current.matched.some((record) => record.meta.public === true)) {
    return (Array.isArray(carried) ? carried[0] : carried) ?? current.fullPath;
  }
  if (current.matched.length > 0) return current.fullPath;
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

/**
 * `?redirect=` 로 받은 값이 앱 안의 로그인 필요 화면 경로일 때만 라우터가 해석한 `fullPath`(대소문자 · 끝 `/` 정규화)로 돌려준다.
 * 다른 사이트(`//evil.com` · `https://…`), 공개 화면(로그인 · 회원가입 · 입력 링크 — 로그인 뒤에 갈 곳이 아니다), 모르는 경로(catch-all 404 로
 * 되돌려지는 경로)와 에러 화면은 거절한다(open redirect 방지). 거절하면 undefined — 호출자가 기본 화면(대시보드)을 쓴다.
 */
export function safeRedirectPath(value: unknown): string | undefined {
  const path = Array.isArray(value) ? value[0] : value;
  if (typeof path !== "string" || !path.startsWith("/")) return undefined;
  if (path.startsWith("//") || path.startsWith("/\\")) return undefined;
  const resolved = router.resolve(path);
  if (resolved.matched.length === 0) return undefined;
  const rejected = resolved.matched.some(
    (record) =>
      record.meta.public === true ||
      record.redirect !== undefined ||
      record.path.startsWith("/error"),
  );
  return rejected ? undefined : resolved.fullPath;
}

/** 인터셉터를 타지 않는 순수 axios 인스턴스 (토큰 갱신/로그아웃 전용) */
function authAxios() {
  return axios.create({
    baseURL: API_HOST,
    headers: {
      "Content-Type": "application/json",
    },
  });
}

/**
 * 백엔드 계약: `GET api/v1/auth/refresh`, `Authorization: Bearer <refreshToken>`
 * `workspaceId` 를 주면 해당 워크스페이스로 전환된 액세스 토큰을 발급한다.
 * 리프레시 토큰이 없거나 만료면(`getValidatedRefreshToken` 이 세션 만료 처리를 이미 했다) 빈 `Bearer ` 로 한 번 더 보내지 않고 실패로 끝낸다.
 * 모든 재발급(자동 · 조용한 · 전환 · 소속 해제 복구)의 단일 관문이라 여기서도 탭 세션을 확인한다(수정 라운드 L1) — 다른 탭이 바꾼 공용
 * 리프레시로 이 탭이 고른 워크스페이스 토큰을 받아 공용 저장소에 쓰면 정상이던 다른 탭이 거꾸로 막힌다. 이 탭의 정상 전환은 고정값(지금
 * 워크스페이스)과 공용 토큰이 같은 상태에서 부르고, 고정값은 그 뒤 `applyWorkspaceSwitch` 가 옮기므로 통과한다.
 */
async function requestRefresh(workspaceId?: number): Promise<JwtTokens> {
  assertTabSession();
  const refreshToken = await getValidatedRefreshToken();
  if (!refreshToken) {
    throw new CanceledError("Refresh token expired");
  }
  return (
    await authAxios().get<JwtTokens>("api/v1/auth/refresh", {
      headers: {
        Authorization: `Bearer ${refreshToken}`,
      },
      params: workspaceId ? { workspaceId } : undefined,
    })
  ).data;
}

const RENEWAL_FAILURE_TOAST_INTERVAL_MS = 4_000;
let lastRenewalFailureToastAt = 0;

/** 재발급 연결 실패 안내(같은 순간 만료된 요청 여럿이 한 재발급을 기다리므로 짧게 한 번만). */
function notifyRenewalConnectionFailure(): void {
  const now = Date.now();
  if (now - lastRenewalFailureToastAt < RENEWAL_FAILURE_TOAST_INTERVAL_MS) {
    return;
  }
  lastRenewalFailureToastAt = now;
  toastWarning(
    "서버에 연결하지 못해 로그인을 연장하지 못했고, 요청을 보내지 않았습니다. 입력한 내용은 화면에 그대로 있으니 잠시 뒤 다시 시도해 주세요.",
  );
}

/** 소속 해제로 다른 워크스페이스로 옮겼을 때의 안내(`applyMe` 강제 전환과 자동 재발급 경로가 함께 쓴다). */
export function workspaceRemovedMessage(workspaceName: string): string {
  return `소속이 해제되어 ${workspaceName} 워크스페이스로 전환했습니다.`;
}

/** 재발급 실패의 401 사유 코드(`INVALID_WORKSPACE_ACCESS` · `NOT_JOINED_WORKSPACE` 비교용) */
function refreshFailureCode(e: unknown): unknown {
  if (!axios.isAxiosError(e) || e.response?.status !== 401) return undefined;
  return (e.response.data as { code?: unknown } | undefined)?.code;
}

/**
 * 소속이 하나도 없다(401 `NOT_JOINED_WORKSPACE`). 서버는 소속이 0 이면 워크스페이스를 지정해도 `INVALID_WORKSPACE_ACCESS` 가 아니라 이 코드를
 * 준다(`app/services/auth.py` `refresh`) — 옮겨 갈 워크스페이스가 없는 제거다(90차 AU90-06).
 */
function isNotJoinedAnyWorkspace(e: unknown): boolean {
  return refreshFailureCode(e) === "NOT_JOINED_WORKSPACE";
}

/** 재발급 실패가 "지금 워크스페이스에 소속이 없음"(401 `INVALID_WORKSPACE_ACCESS` · 소속 0 이면 `NOT_JOINED_WORKSPACE`)인가. */
function isWorkspaceAccessRevoked(e: unknown): boolean {
  return (
    refreshFailureCode(e) === "INVALID_WORKSPACE_ACCESS" ||
    isNotJoinedAnyWorkspace(e)
  );
}

/** 진행 중인 소속 해제 복구(동시에 만료된 요청 여럿이 한 번만 전환하도록 공유한다). */
let removalRecovery: Promise<JwtTokens | undefined> | null = null;

/**
 * 자동 재발급 중 지금 워크스페이스에서 소속이 해제된 것을 알았을 때 다른 소속으로 옮긴다
 * (4차 교차 연계 보완 W5-02 — 3차 Z3-01 은 앱 마운트의 `applyMe` 에만 있어 쓰는 도중 제거되면 로그아웃됐다).
 * - 워크스페이스 없이 `requestRefresh()` 를 **한 번만** 더 부른다. 서버는 첫 소속으로 발급하고, 소속이 하나도
 *   없으면 401 `NOT_JOINED_WORKSPACE` 로 실패한다. 서버는 워크스페이스 검사를 리프레시 토큰 교체 전에 하므로
 *   앞선 401 이 리프레시 토큰을 소모하지 않는다(`app/services/auth.py` `refresh`).
 * - 새 액세스 토큰으로 `GET api/v1/auth/me` 를 불러 소속 목록(해제된 워크스페이스가 빠진)과 전환 대상의
 *   이름 · 요금제를 받는다. 전환 절차와 안내 문구는 `applyMe` 의 강제 전환과 같다.
 * - 어느 단계든 실패하면 `undefined` — 호출부가 지금처럼 로그인 화면으로 보낸다. 다시 시도하지 않는다.
 * - 다른 요청이 이미 옮겼으면(워크스페이스가 바뀌어 있음) 다시 옮기지 않고 지금 토큰을 돌려준다.
 * - 저장하지 않은 편집이 있으면 옮기지 않고 제거 안내를 띄운 뒤 `undefined` 를 돌려준다(62차 BC62-01, `deferWorkspaceRemoval`) — 전환은
 *   화면을 다시 마운트해 편집을 버린다. 사용자가 복사한 뒤 안내의 「전환」 으로 옮긴다. 안내가 떠 있으니 호출부는 로그인으로 보내지 않는다.
 * - 사용 중 미리 재발급(`utils/session.ts`)이 제거를 알았을 때도 부른다(62차 BC62-03).
 */
export async function recoverFromWorkspaceRemoval(
  removedWorkspaceId: number,
): Promise<JwtTokens | undefined> {
  if (removalRecovery) return removalRecovery;
  const authStore = useAuthStore();
  if (authStore.workspace.id && authStore.workspace.id !== removedWorkspaceId) {
    return { ...authStore.tokens };
  }
  removalRecovery = (async () => {
    try {
      const newTokens = await requestRefresh();
      const me = (
        await authAxios().get<AuthMeResponse>("api/v1/auth/me", {
          headers: { Authorization: `Bearer ${newTokens.accessToken}` },
        })
      ).data;
      const target = me.workspaces.find((item) => item.id === me.workspaceId);
      if (!target) return undefined;
      authStore.setWorkspaces(me.workspaces);
      // 그사이 다른 탭 안내가 떴으면 그 안내(새로고침)가 우선이다 — 옮기거나 리프레시를 저장하면 다른 탭의 공용 토큰을 덮는다.
      if (sessionNotice.value) return undefined;
      if (deferWorkspaceRemoval(target, newTokens)) return undefined;
      if (!(await applyWorkspaceSwitch(target, newTokens))) return undefined;
      toastWarning(workspaceRemovedMessage(target.name));
      return newTokens;
    } catch (e: unknown) {
      // 소속이 하나도 남지 않았다 — 옮겨 갈 곳이 없다. 저장하지 않은 편집이 있으면 안내로 머물고(복사할 기회), 없으면 호출부가 로그인으로 보낸다(AU90-06).
      if (isNotJoinedAnyWorkspace(e)) {
        deferRemovalWithoutWorkspace();
        return undefined;
      }
      logger.error("Workspace switch after removal failed:", e);
      return undefined;
    }
  })().finally(() => {
    removalRecovery = null;
  });
  return removalRecovery;
}

/**
 * 만료된 액세스 토큰을 재발급한다(자동 갱신 경로).
 * - 현재 워크스페이스가 있으면 같은 워크스페이스 스코프로 재발급받는다.
 * - 그 워크스페이스에서 소속이 해제됐으면(401 `INVALID_WORKSPACE_ACCESS`) 다른 소속으로 한 번 옮겨 본다
 *   (`recoverFromWorkspaceRemoval`, 4차 W5-02).
 * - 그 밖의 실패는 세션을 신뢰할 수 없으므로 로그인 페이지로 보낸다(저장하지 않은 편집이 있으면 만료 안내부터, 30차 S30-02).
 */
export async function getNewToken(): Promise<JwtTokens | undefined> {
  const workspaceId = useAuthStore().workspace.id;
  try {
    return await requestRefresh(workspaceId || undefined);
  } catch (e: unknown) {
    // 리프레시 토큰이 없거나 만료라 보내지 않은 재발급(`requestRefresh`)은 `getValidatedRefreshToken` 이 세션 만료 처리를 이미 했다 —
    // 오류 로그 · 두 번째 로그인 이동 없이 끝낸다(30차 P30-04). 탭 세션 안내로 막은 경우도 안내가 이미 떠 있다.
    if (axios.isCancel(e)) return undefined;
    // 연결 실패 · 서버 오류(5xx · 응답 없음)는 세션이 끝난 게 아니다(리프레시는 살아 있다) — 만료 안내 · 로그인 이동 없이 이 요청만
    // 보내지 않고 알린다. 다음 동작에서 다시 재발급한다(수정 라운드 L3).
    if (axios.isAxiosError(e) && (!e.response || e.response.status >= 500)) {
      logger.warn("Token renewal failed (connection):", e);
      notifyRenewalConnectionFailure();
      return undefined;
    }
    if (workspaceId && isWorkspaceAccessRevoked(e)) {
      const recovered = await recoverFromWorkspaceRemoval(workspaceId);
      if (recovered) return recovered;
      // 저장하지 않은 편집이 있어 제거 안내를 띄우고 전환을 미뤘다(62차 BC62-01) — 세션은 살아 있다.
      if (sessionNotice.value?.kind === "removed") return undefined;
    }
    logger.error("Token renewal failed:", e);
    try {
      // 소속이 하나도 없어 제거된 것이면(편집이 있으면 위에서 안내로 머물렀다) 「제거」 사유로 로그인에 보낸다
      if (workspaceId && isNotJoinedAnyWorkspace(e)) {
        await expireSession("/", LOGIN_REASON_REMOVED);
      } else {
        await expireSession(currentPathForRedirect());
      }
    } catch (loginError) {
      logger.error("Login redirect failed:", loginError);
    }
    return undefined;
  }
}

/**
 * 만료 전에 미리 맞추는 조용한 토큰 재발급(`applyMe` 의 역할 동기화 전용, 2차 교차 연계 보완 Y3-06).
 * - `getNewToken()` 과 달리 실패해도 로그인 페이지로 보내지 않는다 — 호출 시점의 액세스 토큰은 아직
 *   유효해서(만료가 아니라 역할만 어긋난 상태) 세션이 끊긴 게 아니기 때문이다. 실패는 경고만 남기고
 *   다음 기회(다음 `applyMe` 호출, 또는 실제 만료 시 `getValidatedAccessToken`)에 다시 맞춘다.
 * - 사용 중 미리 재발급(`utils/session.ts`, 30차 S30-02)도 쓴다. 리프레시 토큰이 이미 만료면 부르지 않는다 — `requestRefresh` 의
 *   `getValidatedRefreshToken` 은 만료를 보면 로그인 이동(`signOut`)까지 하기 때문이다.
 */
export async function getNewTokenQuietly(): Promise<JwtTokens | undefined> {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken || isExpiredToken(refreshToken)) return undefined;
  resetQuietRenewalResult();
  try {
    const { workspace } = useAuthStore();
    return await requestRefresh(workspace.id || undefined);
  } catch (e: unknown) {
    logger.warn("Quiet token renewal failed:", e);
    quietRenewalStatus = axios.isAxiosError(e) ? (e.response?.status ?? 0) : 0;
    quietRenewalRevoked = isWorkspaceAccessRevoked(e);
    quietRenewalNotJoined = isNotJoinedAnyWorkspace(e);
    return undefined;
  }
}

/** 마지막 조용한 재발급 실패의 HTTP 상태(연결 실패 0, 성공 · 시도 전 null). 미리 재발급이 401 이면 그 리프레시로는 다시 시도하지 않는다(L4). */
let quietRenewalStatus: number | null = null;

export function lastQuietRenewalStatus(): number | null {
  return quietRenewalStatus;
}

/** 마지막 조용한 재발급 실패가 401 `INVALID_WORKSPACE_ACCESS`(지금 워크스페이스에서 제거됨)인가(62차 BC62-03). */
let quietRenewalRevoked = false;

export function quietRenewalRevokedWorkspace(): boolean {
  return quietRenewalRevoked;
}

/** 마지막 조용한 재발급 실패가 401 `NOT_JOINED_WORKSPACE`(소속이 하나도 없음)인가 — 로그인 사유를 「제거」 로 보낸다(90차 C90-03c). */
let quietRenewalNotJoined = false;

export function quietRenewalNotJoinedAnyWorkspace(): boolean {
  return quietRenewalNotJoined;
}

/**
 * 조용한 재발급 결과를 지운다. 미리 재발급은 진행 중인 재발급(`reIssueAccessToken` 단일 비행)에 합류하면 `getNewTokenQuietly` 를 부르지
 * 않으므로, 부르기 전에 지워 이전 실패(거짓 401 · 제거됨)를 읽지 않게 한다(62차 리뷰 R1).
 */
export function resetQuietRenewalResult(): void {
  quietRenewalStatus = null;
  quietRenewalRevoked = false;
  quietRenewalNotJoined = false;
}

/**
 * 워크스페이스 전환용 토큰 재발급.
 * - 전환 실패(예: 소속되지 않은 워크스페이스)는 세션 문제가 아니므로 로그아웃시키지 않고
 *   `undefined` 를 돌려준다. 호출부가 토스트로 알린다.
 */
export async function getTokenForWorkspace(
  workspaceId: number,
): Promise<JwtTokens | undefined> {
  try {
    return await requestRefresh(workspaceId);
  } catch (e: unknown) {
    logger.error("Workspace switch failed:", e);
    return undefined;
  }
}

/**
 * 워크스페이스 전환 공통 절차(`DefaultLayout.vue` 의 사용자가 고른 전환과 `stores/auth.ts` `applyMe` 의
 * 제거 뒤 강제 전환이 함께 쓴다, 3차 교차 연계 보완 Z3-01 리뷰 보완).
 * - 쿼리(`?jobId=` 등)는 이전 워크스페이스의 것이라 비운다. 같은 경로 · 쿼리만 바뀌는 이동이라 화면의 `onBeforeRouteUpdate`
 *   (레퍼런스 체크 「지원자 전환」 · 면접 콘솔 저장 확인)가 돌므로 그동안 업데이트 가드를 뗀다(59차 AR59-01) — 사용자 전환은 이미
 *   `confirmLeaveCurrentPage` 로 확인받았고 강제 전환은 물을 대상이 아니다. 예전에는 확인창이 두 번 떴고, 두 번째를 「취소」 해도 전환은
 *   진행되며 옛 워크스페이스의 `?applicantId=` 가 주소에 남았다. 가드는 이동 뒤 되돌린다(화면이 곧 재마운트되며 옛 가드는 언마운트 때 빠진다).
 * - 쿼리를 비우지 못했으면(다른 이동이 끼어든 `NavigationFailure`) 전환하지 않고 `false` 를 돌려준다. 이미 회전된 리프레시 토큰만
 *   저장해(서버는 옛 리프레시를 10초 뒤 거절한다) 지금 워크스페이스 세션은 그대로 이어진다.
 * - 이전 워크스페이스 기준으로 진행 중이던 요청을 모두 취소한다.
 * - `setWorkspace` · `saveTokens` 순서로 스토어를 새 워크스페이스로 맞춘다.
 * 화면을 떠나도 되는지 묻는 것(`confirmLeaveCurrentPage`)과 결과 토스트는 호출부 몫이다 — 강제 전환은
 * 물을 대상이 없고, 토스트 문구도 전환 사유에 따라 다르다.
 */
export async function applyWorkspaceSwitch(
  target: Workspace,
  newTokens: JwtTokens,
): Promise<boolean> {
  if (Object.keys(router.currentRoute.value.query).length > 0) {
    const records = router.currentRoute.value.matched;
    const saved = records.map((record) => [...record.updateGuards]);
    records.forEach((record) => {
      record.updateGuards.clear();
    });
    let failure;
    try {
      failure = await router.replace({ path: router.currentRoute.value.path });
    } finally {
      // 끼어든 이동으로 다른 화면에 왔으면 옛 레코드에 되돌리지 않는다 — 그 화면은 가드를 뗀 채 언마운트돼 빼지 못한 옛 가드가 남는다.
      const current = router.currentRoute.value.matched;
      records.forEach((record, index) => {
        if (!current.includes(record)) return;
        saved[index]?.forEach((guard) => record.updateGuards.add(guard));
      });
    }
    if (failure) {
      logger.warn("Workspace switch aborted: query not cleared", failure);
      useAuthStore().saveRefreshToken(newTokens.refreshToken);
      return false;
    }
  }
  pendingRequests.forEach((controller) => {
    controller.abort(`Workspace switch to ${String(target.id)}`);
  });
  pendingRequests.clear();
  // 옛 워크스페이스에서 기억한 화면 문맥(마지막 직무 · 콘솔 필터)은 새 워크스페이스에 맞지 않는다(73차 DA73-03 · 01).
  forgetWorkspaceViewState();
  const { saveTokens, setWorkspace, applyPendingRole } = useAuthStore();
  // `setWorkspace` 를 먼저 불러야 `saveTokens` 가 새 워크스페이스 기준으로 역할을 토큰 역할에 맞춘다
  // (`target.role` 은 마지막 `/auth/me` 목록의 값이라 그 뒤 바뀐 역할을 모른다, 4차 교차 연계 보완 W3-03).
  // 전환은 화면을 어차피 새로 마운트하므로 올라간 역할도 보류하지 않고 바로 반영한다(4차 M2).
  setWorkspace(target);
  saveTokens(newTokens);
  applyPendingRole();
  return true;
}

/**
 * 요청에 실을 액세스 토큰. 공용 토큰이 이 탭의 세션(사용자 · 워크스페이스)과 다르면 먼저 막는다(`assertTabSession`, 30차 S30-01) — 만료된
 * 다른 탭 토큰을 이 탭 워크스페이스로 재발급하면 다른 탭의 세션을 뒤집는다.
 */
export async function getValidatedAccessToken(
  method?: string,
): Promise<string> {
  assertTabSession();
  const accessToken = tokenStorage.getAccessToken();
  // 세션이 끝났으면(토큰 없음 · 재발급 실패) 세션 만료 처리를 맡기고 요청은 보내지 않는다(30차 P30-04). 예전에는 빈 토큰으로 인증 헤더 없이
  // 나가 401 이 한 번 더 났다(대기 중이던 화면 요청마다).
  if (!accessToken || accessToken === "undefined") {
    await signOut();
    throw new CanceledError("Session ended");
  }
  if (!isExpiredToken(accessToken)) return accessToken;

  try {
    const { reIssueAccessToken } = useAuthStore();
    await reIssueAccessToken();
  } catch (e: unknown) {
    // 재발급 중 다른 워크스페이스로 옮긴 경우(4차 W5-02)는 세션이 살아 있다 — 이 요청만 취소한다.
    if (axios.isCancel(e)) throw e;
    await signOut();
    throw new CanceledError("Token renewal failed");
  }
  // 재발급이 실패했으면(`getNewToken` 이 세션 만료 처리를 했다) 토큰이 지워졌거나 만료된 그 토큰 그대로다.
  const renewed = tokenStorage.getAccessToken();
  if (!renewed || renewed === accessToken) {
    throw new CanceledError("Session ended");
  }
  cancelWriteIfRoleLowered(accessToken, renewed, method);
  return renewed;
}

function claimedRole(token: string | undefined): WorkspaceRole | null {
  if (!token) return null;
  try {
    return jwtDecode<TokenClaims>(token).role ?? null;
  } catch {
    return null;
  }
}

/**
 * 재발급이 역할 강등을 드러내면(받은 새 토큰의 역할이 요청이 쥐고 있던 토큰의 역할보다 낮으면) 쓰기 요청은 보내지 않고 취소로 끝낸다
 * (83차 JR83-05). 예전에는 요청이 새(낮은) 토큰으로 나가려는 사이 화면이 역할 변경 다시 마운트를 시작해 이탈 가드 토스트 둘만 남고, 누른 동작은
 * 결과 문장 없이 사라졌다. 읽기(GET)는 그대로 보낸다 — 가려진 값으로 다시 읽는 것이 맞다. 취소 사유 `Role lowered` 는 요청 인터셉터가 다른
 * 취소처럼 토스트 없는 `success: false` 로 돌려준다(문장은 여기서 한 번만).
 * @param before 요청이 쥐고 있던(만료된) 액세스 토큰
 * @param after 재발급으로 받은 액세스 토큰
 */
/** 강등으로 쓰기를 취소한 때(ms) — 쓰기를 시도했다는 건 입력이 있었다는 뜻이라 자동 다시 불러오기가 그 입력을 지우면 안 된다(89차 RVDS89-02) */
let loweredWriteCanceledAt = 0;

/** 강등으로 취소된 쓰기가 있었는지 한 번 읽고 지운다 */
export function consumeLoweredWriteCancel(): boolean {
  const canceled = loweredWriteCanceledAt > 0;
  loweredWriteCanceledAt = 0;
  return canceled;
}

/** 강등 취소 토스트를 미루는 시간 — 낮아진 역할로 화면이 다시 그려져 편집 칸이 닫혔는지 본 뒤에 문장을 정한다 */
const ROLE_LOWERED_TOAST_DELAY_MS = 150;

export function cancelWriteIfRoleLowered(
  before: string | undefined,
  after: string,
  method?: string,
): void {
  if (!method || method.toLowerCase() === "get") return;
  const was = claimedRole(before);
  const now = claimedRole(after);
  if (!was || !now || ROLE_RANK[now] >= ROLE_RANK[was]) {
    return;
  }
  loweredWriteCanceledAt = Date.now();
  // 재발급이 저장한 낮은 역할로 화면이 다시 그려진 뒤(편집 칸이 닫히는 화면이 있다) 입력이 남아 있는지 보고 문장을 정한다(RVFA90-02) —
  // 칸이 사라졌는데 「화면에 그대로 있으니」 라고 하면 거짓이다.
  setTimeout(() => {
    toastWarning(
      editedFieldVanished()
        ? "권한이 줄어 변경 요청을 보내지 않았고 저장되지 않았습니다. 권한이 바뀌어 편집 칸이 닫혔으니, 필요하면 화면을 다시 불러온 뒤 다시 입력해 주세요."
        : "권한이 줄어 변경 요청을 보내지 않았습니다. 입력한 내용이 있으면 화면에 그대로 있으니 옮겨 적은 뒤 화면을 다시 불러와 주세요.",
      { id: "role-lowered" },
    );
  }, ROLE_LOWERED_TOAST_DELAY_MS);
  throw new CanceledError("Role lowered");
}

export async function getValidatedRefreshToken(): Promise<string> {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken || isExpiredToken(refreshToken)) {
    await signOut();
    return "";
  }
  return refreshToken;
}
